import { useItemSeenRef } from '@wix/data-tools-generic-events';
import { shouldLogBi } from './bi-logger';
import { toAdditionalFields } from './use-generic-bi';

/**
 * `itemSeen` (198:6) for a widget that came into VIEW — the impression half of a widget funnel, whose
 * other half is the `clickCta` (198:10) fired by `useGenericBi().logPageClickBi`.
 *
 * Returns a callback ref to attach to the element being measured. The platform hook does the
 * IntersectionObserver work: by default it fires ONCE, when 50% of the element is visible.
 *
 * **Why viewport and not a mount effect.** A widget rendered below the fold, or into a closed sheet, has
 * not been seen — and an impression that counts those inflates the denominator of every conversion rate
 * measured against it. This is also what makes it different from `logLoadFinish` (198:5), which says a
 * region finished fetching and can fire entirely off-screen.
 *
 * Wrapped rather than called directly at the call site so an impression gets the same two things every
 * other event in this app gets: the `shouldLogBi()` surface gate, and `toAdditionalFields` normalization.
 * Calling the platform hook raw would quietly opt out of both.
 */
export function useItemSeenBi(
  componentName: string,
  opts?: { additionalFields?: Record<string, string | undefined>; disabled?: boolean },
): (node: Element | null) => void {
  return useItemSeenRef(
    {
      componentName,
      ...(opts?.additionalFields ? { additionalFields: toAdditionalFields(opts.additionalFields) } : {}),
    },
    // `disabled` is the platform hook's own opt-out, so a gated-off surface never even observes.
    { disabled: opts?.disabled === true || !shouldLogBi() },
  );
}
